import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useTranslation } from "react-i18next";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";

const ImageManagementDialog = ({
  open,
  onClose,
  treatmentId,
  existingImages = [],
  removedImageIds = [],
  onImageUpdated,
  onRemoveExistingImage,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [newImages, setNewImages] = useState([]);
  const [imageErrors, setImageErrors] = useState([]);

  const { handleUpdate, loading } = useUpdate("dental-treatment-images", token);

  const handleNewImageChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const updatedImages = [...newImages];
      updatedImages[index] = file;
      setNewImages(updatedImages);
    }
  };

  const addNewImageField = () => {
    setNewImages([...newImages, null]);
  };

  const removeNewImageField = (index) => {
    const updatedImages = [...newImages];
    updatedImages.splice(index, 1);
    setNewImages(updatedImages);
    const updatedErrors = [...imageErrors];
    updatedErrors.splice(index, 1);
    setImageErrors(updatedErrors);
  };

  const validateImages = () => {
    let isValid = true;
    const errors = [];
    newImages.forEach((image, index) => {
      if (!image) {
        errors[index] = t("dental.imageRequired");
        isValid = false;
      } else if (image.size > 2 * 1024 * 1024) {
        errors[index] = t("dental.imageTooLarge");
        isValid = false;
      } else if (!image.type.startsWith("image/")) {
        errors[index] = t("dental.invalidImageType");
        isValid = false;
      }
    });
    setImageErrors(errors);
    return isValid;
  };

  // Handle save operation
  const handleSave = async () => {
    if (!validateImages()) {
      return;
    }

    const formData = new FormData();
    formData.append("treatment_id", treatmentId);

    removedImageIds.forEach((imageId) => {
      formData.append("remove_images", imageId);
    });

    newImages.forEach((image) => {
      if (image) formData.append("new_images", image);
    });

    handleUpdate(treatmentId, formData);
    onImageUpdated();
    setNewImages([]);
    onClose();
  };

  useEffect(() => {
    return () => {
      newImages.forEach((image) => {
        if (image) URL.revokeObjectURL(URL.createObjectURL(image));
      });
    };
  }, [newImages]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: "bold", color: "#1976d2" }}>
        {t("dental.imageManagement")}
      </DialogTitle>
      <DialogContent>
        {/* Existing Images Section */}
        <Typography variant="h6" gutterBottom>
          {t("dental.existingImages")}
        </Typography>
        {existingImages.length > 0 ? (
          existingImages.map((image) => (
            <Box
              key={image.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px",
                backgroundColor: "#f5f5f5",
                borderRadius: "5px",
                marginBottom: "10px",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <img
                  src={image.image}
                  alt={t("dental.existingImage")}
                  style={{
                    height: "40px",
                    width: "40px",
                    borderRadius: "5px",
                    marginRight: "10px",
                    objectFit: "cover",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: "500" }}>
                  <a
                    href={image.image}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "#1976d2" }}
                  >
                    {image.image.split("/").pop()}
                  </a>
                </Typography>
              </Box>
              <IconButton
                color="error"
                onClick={() => {
                  onRemoveExistingImage(image.id);
                }}
              >
                <HighlightOffIcon />
              </IconButton>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("dental.noExistingImages")}
          </Typography>
        )}

        {/* New Images Section */}
        <Typography variant="h6" gutterBottom sx={{ marginTop: "20px" }}>
          {t("dental.addNewImages")}
        </Typography>
        {newImages.map((image, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
              backgroundColor: "#f5f5f5",
              borderRadius: "5px",
              padding: "10px",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <TextField
                type="file"
                onChange={(e) => handleNewImageChange(e, index)}
                inputProps={{ accept: "image/*" }}
                sx={{ flex: 1 }}
              />
              <IconButton
                color="error"
                onClick={() => removeNewImageField(index)}
                sx={{ ml: 1 }}
              >
                <HighlightOffIcon />
              </IconButton>
            </Box>
            {newImages[index] && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={URL.createObjectURL(newImages[index])}
                  alt={`Preview ${index}`}
                  style={{
                    height: "80px",
                    width: "80px",
                    borderRadius: "5px",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}
            {imageErrors[index] && (
              <Typography color="error" fontSize="small">
                {imageErrors[index]}
              </Typography>
            )}
          </Box>
        ))}
        <Button
          onClick={addNewImageField}
          startIcon={<AddIcon />}
          variant="outlined"
          sx={{
            marginTop: "10px",
            color: "#1976d2",
            borderColor: "#1976d2",
            "&:hover": { backgroundColor: "#f0f8ff" },
          }}
        >
          {t("dental.addImage")}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            borderColor: "#9e9e9e",
          }}
        >
          {t("dental.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#125ca8" },
          }}
          disabled={loading}
        >
          {loading ? t("dental.saving") : t("dental.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageManagementDialog;
